import React, { useContext } from 'react';
import { LuBell, LuMoon, LuSun } from 'react-icons/lu';
import GreenChargeLogo from '../components/Common/GreenChargeLogo';
import { useUnreadNotifications } from '../hooks/useUnreadNotifications';
import { useOptionalNotificationDrawer } from './NotificationDrawer/NotificationDrawerContext.jsx';
import { ThemeContext } from '../theme/theme.jsx';
import './MobileTopBar.less';

/**
 * Inline theme toggle scoped to the mobile top bar.
 *
 * Reads ThemeContext directly (mirroring the pattern in
 * src/theme/ThemeToggle.jsx) so this button renders nothing when the
 * bar is mounted without a <ThemeProvider>. Uses the topbar's own
 * `.gc-mobile-topbar-action` shell so the sun/moon icon shares the
 * 44×44 touch target, hover/active feedback, and vertical alignment
 * with the notifications bell to its right.
 *
 * The current theme is signalled with `is-active` — the token layer
 * gives it a soft brand-tint background so the active state reads
 * without the icon itself changing colour.
 */
function TopBarThemeToggle() {
  const ctx = useContext(ThemeContext);
  if (!ctx) return null;
  const { resolved, toggle } = ctx;
  const isDark = resolved === 'dark';
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';
  return (
    <button
      type="button"
      className="gc-mobile-topbar-action gc-mobile-topbar-action--theme is-active"
      onClick={toggle}
      aria-label={label}
      aria-pressed={isDark}
      title={label}
    >
      {isDark
        ? <LuSun aria-hidden="true" className="gc-mobile-topbar-action-icon" />
        : <LuMoon aria-hidden="true" className="gc-mobile-topbar-action-icon" />}
    </button>
  );
}

/**
 * MobileTopBar — the mobile-only app-chrome header. Sits above the
 * scrolling content on phones the same way MobileBottomNav sits
 * below it. Deliberately NOT a website navbar: no back button, no
 * search, no menu — just the official GreenCharge brand mark on
 * the left and a single bell action on the right.
 *
 * Branding uses <GreenChargeLogo> so we get the same theme-aware
 * wordmark asset (`greencharge-0.png` / `greencharge-1.png`) that
 * every other branded surface in the app already uses.
 *
 * The bell OPENS the global notification drawer — no route change,
 * no page navigation. Falls back to a hidden state when the shell
 * isn't providing a drawer context (component tests that render it
 * in isolation). Unread badge/dot drives the aria label so
 * assistive tech isn't dot-only.
 */
function MobileTopBar() {
  const { unread, hasUnread } = useUnreadNotifications();
  const drawer = useOptionalNotificationDrawer();
  const isDrawerOpen = drawer.isOpen === true;
  const bellLabel = hasUnread
    ? `Open notifications, ${unread} unread`
    : 'Open notifications';

  return (
    <header className="gc-mobile-topbar" role="banner">
      <div className="gc-mobile-topbar-inner">
        <div className="gc-mobile-topbar-brand">
          <GreenChargeLogo
            variant="wordmark"
            height={36}
            className="gc-mobile-topbar-logo"
          />
        </div>
        <div className="gc-mobile-topbar-actions">
          <TopBarThemeToggle />
          <button
            type="button"
            className={`gc-mobile-topbar-action${isDrawerOpen ? ' is-open' : ''}`}
            onClick={drawer.open}
            aria-label={bellLabel}
            aria-haspopup="dialog"
            aria-expanded={isDrawerOpen}
          >
            <LuBell aria-hidden="true" className="gc-mobile-topbar-action-icon" />
            {hasUnread && (
              <span className="gc-mobile-topbar-action-dot" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

export default MobileTopBar;
